import { XMarkIcon } from "@heroicons/react/20/solid";
import { useTranslation } from "react-i18next";
import type { AccessList, PersonGroupChanges } from "../../../api/access/accessModel";
import AppButton from "../../../ui/buttons/AppButton";
import Chip from "../../../ui/feedback/Chip";
import MenuButton, { type MenuItem } from "../../../ui/overlays/MenuButton";
import { memberAdded, memberRemoved } from "../changes/accessChanges";
import type { AccessChanges } from "../changes/useAccessChanges";
import type { GroupNamer } from "../model/groupNames";

interface PersonGroupsProps {
  list: AccessList;
  person: { readonly userId: string; readonly name: string };
  namer: GroupNamer;
  /** What the server says the viewer may change of the person's groups here. */
  offers: PersonGroupChanges;
  changes: AccessChanges;
}

/**
 * A person's groups here, which give them what they may do (Johannes, 4 Nov: "i would expect i can
 * add their permissions here or groups"). The viewer puts them into a group, an included
 * installation group too, or takes them out of one, exactly as the member routes would accept
 * (`offers`); a group that keeps them whoever asks says why (F12). Each change applies at once,
 * with Undo.
 */
export default function PersonGroups({ list, person, namer, offers, changes }: PersonGroupsProps) {
  const { t } = useTranslation();
  const reaching = list.people.find((listed) => listed.userId === person.userId)?.groupIds ?? [];
  const installationGroups = new Set(list.groups.filter((group) => group.projectId === null).map((group) => group.groupId));
  const named = (groupId: string) => ({ groupId, name: namer.name(groupId) });
  const addItem = (groupId: string): MenuItem => ({
    id: groupId,
    label: namer.name(groupId),
    action: { kind: "select", onSelect: () => { changes.run(memberAdded(t, named(groupId), person)); } },
  });
  const removable = new Set(offers.removable);
  const takeOut = (groupId: string) => {
    const label = t("access.takeOut", { name: person.name, group: namer.name(groupId) });
    const kept = offers.kept.get(groupId);
    if (kept !== undefined) {
      return <AppButton variant="ghost" size="sm" aria-label={label} unavailable={t(`access.kept.${kept}`)}><XMarkIcon className="h-4 w-4" aria-hidden="true" /></AppButton>;
    }
    if (!removable.has(groupId)) return null;
    return (
      <AppButton variant="ghost" size="sm" aria-label={label} disabled={changes.busy} onClick={() => { changes.run(memberRemoved(t, named(groupId), person)); }}>
        <XMarkIcon className="h-4 w-4" aria-hidden="true" />
      </AppButton>
    );
  };

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-2">
        <h4 className="app-label text-xs">{t("access.panel.groupsHere")}</h4>
        <MenuButton
          align="right"
          sections={[
            { id: "project", items: offers.addable.filter((groupId) => !installationGroups.has(groupId)).map(addItem) },
            { id: "installation", heading: t("access.installationGroups"), items: offers.addable.filter((groupId) => installationGroups.has(groupId)).map(addItem) },
          ]}
          renderTrigger={(trigger) => (
            <AppButton {...trigger} variant="secondary" size="sm" disabled={changes.busy}>{t("access.panel.addToGroup")}</AppButton>
          )}
        />
      </div>
      <p className="flex flex-wrap items-center gap-1">
        {reaching.length === 0 ? <span className="app-text-muted text-sm">{t("access.panel.noGroup")}</span> : null}
        {reaching.map((groupId) => (
          <span key={groupId} className="inline-flex items-center gap-0.5">
            <Chip>{namer.name(groupId)}</Chip>
            {takeOut(groupId)}
          </span>
        ))}
      </p>
    </div>
  );
}
